// frontend/src/features/livestock/TankShotViewer.tsx
// One tank shot full screen (design 6.2, 6.4): its date and note, Edit (date
// and note), Delete with a confirm (which says when shared links use the shot),
// Set as cover / Use latest shot, and Line up against the reference shot with
// Reset line up (docs/plans/2026-10-29-tank-shot-framing-design.md, 7.2), also
// on the reference when it still has a crop of its own
// (docs/plans/2026-10-30-organism-reference-reset-and-as-taken.md, 2). The
// photo shows as taken; a lined-up shot has a "Lined up" badge.

import { useState } from 'react';
import { Badge, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { IconFocusCentered, IconFrameOff } from '@tabler/icons-react';
import { tankPhotosApi } from '@/api/tankPhotos';
import { useTank } from '@/contexts/TankContext';
import type { LargePhoto, TankPhoto } from '@/types/tankPhotos';
import type { TimelineEntryUpdate } from '@/types/timeline';
import {
  useDeleteTankPhoto,
  useEditTankPhoto,
  useSetTankCover,
  useSetTankPhotoFraming,
} from '@/hooks/useTankPhotos';
import { EditTimelineEntryModal } from './EditTimelineEntryModal';
import { LineUpModal } from './LineUpModal';
import { linedUpToEarlierReference, lineUpMode, referenceOf } from './lineUpReference';
import { LIGHTBOX_BAR_BUTTON, LIGHTBOX_Z_INDEX, PhotoLightbox } from './PhotoLightbox';
import { formatTakenAt } from './photoInboxFormat';
import { sharedLinkWarning } from './shareLinkActions';

export interface TankShotViewerProps {
  tankId: string;
  /** The open shot; null closes the viewer. */
  shot: TankPhoto | null;
  onClose: () => void;
}

/** Edit and delete open over the full-screen lightbox. */
const OVER_LIGHTBOX = LIGHTBOX_Z_INDEX + 50;
/** The server takes dates from 1990 to a day ahead (plan 3.3). */
const MIN_DATE = new Date(Date.UTC(1990, 0, 1));
const DAY_MS = 24 * 60 * 60 * 1000;

export function TankShotViewer({ tankId, shot, onClose }: TankShotViewerProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [liningUp, setLiningUp] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const edit = useEditTankPhoto(tankId);
  const remove = useDeleteTankPhoto(tankId);
  const cover = useSetTankCover(tankId);
  const framingChange = useSetTankPhotoFraming(tankId);
  const { tanks } = useTank();
  const reference = referenceOf(tanks.find((tank) => tank.id === tankId));
  const isReference = !!shot && lineUpMode(shot.id, reference) === 'reference';
  const framing = shot?.framing ?? null;

  const confirmReset = async (): Promise<void> => {
    if (!shot) return;
    try {
      await framingChange.mutateAsync({ id: shot.id, framing: null });
      setConfirmingReset(false);
    } catch {
      // Notified by the hook; the confirm stays open.
    }
  };

  const save = async (id: number, data: TimelineEntryUpdate): Promise<void> => {
    try {
      await edit.mutateAsync({ id, data });
      setEditing(false);
    } catch {
      // The hook shows what went wrong; the form stays open to try again.
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!shot) return;
    try {
      await remove.mutateAsync(shot.id);
      setConfirmingDelete(false);
      onClose();
    } catch {
      // Notified by the hook; the confirm stays open.
    }
  };

  const caption = shot ? (
    <Stack gap={2}>
      <Text size="sm" fw={500} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatTakenAt(shot.entry_date)}
      </Text>
      {shot.entry_text && (
        <Text size="sm" c="gray.4" lineClamp={3} style={{ whiteSpace: 'pre-line' }}>
          {shot.entry_text}
        </Text>
      )}
      {isReference && (
        <Text size="sm" c="gray.4" data-testid="reference-note">
          This is the reference shot. Other shots are lined up to match it.
        </Text>
      )}
      {isReference && framing && (
        <Text size="sm" c="gray.4" data-testid="cropped-reference-note">
          It is still cropped by an earlier line up, so compare leaves part of it out. Reset its
          line up to use the whole shot.
        </Text>
      )}
      {!isReference && linedUpToEarlierReference(framing, reference) && (
        <Text size="sm" c="gray.4" data-testid="earlier-reference-note">
          Lined up to an earlier reference shot. Line up again to match the current one.
        </Text>
      )}
    </Stack>
  ) : null;

  return (
    <>
      <PhotoLightbox
        opened={!!shot}
        onClose={onClose}
        photoUrl={shot ? (shot.photo_url ?? shot.photo_display_url) : null}
        photoKey={shot?.id}
        loadLarge={
          shot ? (): Promise<LargePhoto | null> => tankPhotosApi.large(tankId, shot.id) : undefined
        }
        alt={shot ? `Tank shot, ${formatTakenAt(shot.entry_date)}` : undefined}
        caption={caption}
        badges={
          framing ? (
            <Badge
              variant="light"
              color="teal"
              leftSection={<IconFocusCentered size={12} />}
              mr="xs"
            >
              Lined up
            </Badge>
          ) : null
        }
        actions={
          shot && (!isReference || framing) ? (
            <>
              {!isReference && (
                <Button
                  {...LIGHTBOX_BAR_BUTTON}
                  leftSection={<IconFocusCentered size={16} />}
                  onClick={() => setLiningUp(true)}
                >
                  Line up
                </Button>
              )}
              {framing && (
                <Button
                  {...LIGHTBOX_BAR_BUTTON}
                  leftSection={<IconFrameOff size={16} />}
                  onClick={() => setConfirmingReset(true)}
                >
                  Reset line up
                </Button>
              )}
            </>
          ) : null
        }
        onEdit={() => setEditing(true)}
        onDelete={() => setConfirmingDelete(true)}
        cover={
          shot
            ? {
                isCover: shot.is_cover,
                onSetCover: () => cover.mutate(shot.id),
                onUseLatest: () => cover.mutate(null),
                pending: cover.isPending,
              }
            : undefined
        }
      />

      <LineUpModal
        tankId={tankId}
        shot={liningUp ? shot : null}
        reference={reference}
        onClose={() => setLiningUp(false)}
      />

      <Modal
        opened={confirmingReset && !!shot}
        onClose={() => setConfirmingReset(false)}
        title="Reset this line up?"
        size="sm"
        centered
        zIndex={OVER_LIGHTBOX}
      >
        <Stack gap="md">
          <Text size="sm">
            The shot shows as taken again in Compare and in shared links. The photo itself is never
            changed.
          </Text>
          {isReference && (
            <Text size="sm" data-testid="reset-reference-warning">
              Shots already lined up to it were matched to its cropped view, so line them up again
              afterwards.
            </Text>
          )}
          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setConfirmingReset(false)}
              disabled={framingChange.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => void confirmReset()} loading={framingChange.isPending}>
              Reset
            </Button>
          </Group>
        </Stack>
      </Modal>

      <EditTimelineEntryModal
        entry={shot}
        opened={editing && !!shot}
        onClose={() => setEditing(false)}
        onSave={save}
        isSaving={edit.isPending}
        title="Edit tank shot"
        minDate={MIN_DATE}
        maxDate={new Date(Date.now() + DAY_MS)}
        zIndex={OVER_LIGHTBOX}
      />

      <Modal
        opened={confirmingDelete && !!shot}
        onClose={() => setConfirmingDelete(false)}
        title="Delete this tank shot?"
        size="sm"
        centered
        zIndex={OVER_LIGHTBOX}
      >
        <Stack gap="md">
          <Text size="sm">
            {shot ? `The shot from ${formatTakenAt(shot.entry_date)} is deleted for good.` : ''}
            {shot?.is_cover ? ' It is the cover: the latest shot becomes the cover.' : ''}
          </Text>
          {shot && shot.share_link_count > 0 && (
            <Text size="sm" c="coral.7" fw={500} data-testid="delete-share-warning">
              {sharedLinkWarning(shot.share_link_count)}
            </Text>
          )}
          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setConfirmingDelete(false)}
              disabled={remove.isPending}
            >
              Cancel
            </Button>
            <Button color="red" onClick={() => void confirmDelete()} loading={remove.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
